/**
 * Celula de suma din grila: afiseaza valoarea, iar cand cursorul intra in editare devine input.
 *
 * Diferenta fata de `AmountInput` (folosit in formulare) e ca aici textul in lucru NU aparține
 * componentei, ci masinii de cursor: la eroare de salvare celula rămâne deschisa cu ce s-a tastat,
 * iar `Enter` / `Tab` / `Esc` sunt tratate de grila, nu de input. Altfel doua surse de adevar ar
 * lupta pentru acelasi text.
 *
 * Indicatorul de salvare e un punct de 2px in coltul din dreapta-sus: `dirty` -> `saving` ->
 * `saved`, care se stinge singur. Niciodata spinner blocant, niciodata modal de salvare.
 */

import { useEffect, useRef } from 'react';
import { Money } from '../../components/Money.tsx';

export type StareSalvare = 'curat' | 'modificat' | 'in-curs' | 'salvat' | 'eroare';

export interface PropsCelulaSuma {
  minor: number | null;
  moneda?: string | null;
  scalaMinora?: number;
  /** Expresia din care a rezultat suma (`=25422,5+4842,87`), daca exista. */
  expresie?: string | null;
  /** Numarul de zecimale afisate. `0` in grile dense, `2` in drawer. */
  zecimale?: 0 | 2;

  seEditeaza: boolean;
  textEditare: string;
  /** Cursorul de text sta la final cand editarea a inceput prin type-to-fill. */
  laFinal: boolean;
  laSchimbare: (text: string) => void;
  /** `blur` inseamna commit, ca in Excel: click in alta parte salveaza. */
  laBlur: () => void;

  stareSalvare?: StareSalvare;
  mesajEroare?: string | null;
  eticheta: string;
}

export function CelulaSuma({
  minor,
  moneda = 'lei',
  scalaMinora = 100,
  expresie = null,
  zecimale = 2,
  seEditeaza,
  textEditare,
  laFinal,
  laSchimbare,
  laBlur,
  stareSalvare = 'curat',
  mesajEroare = null,
  eticheta,
}: PropsCelulaSuma) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!seEditeaza) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    // Type-to-fill: cursorul dupa caracterul tastat. Editare deschisa cu `Enter`/`F2`: tot textul
    // selectat, ca o suma noua sa il inlocuiasca dintr-o data.
    if (laFinal) el.setSelectionRange(el.value.length, el.value.length);
    else el.select();
  }, [seEditeaza, laFinal]);

  if (seEditeaza) {
    return (
      <span className="celula-editare relative flex items-center justify-end gap-1">
        <input
          ref={ref}
          className="input num w-full text-right"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={textEditare}
          aria-label={eticheta}
          aria-invalid={stareSalvare === 'eroare'}
          onChange={(e) => laSchimbare(e.target.value)}
          onBlur={laBlur}
        />
        {moneda ? <span className="text-11 text-ink-3">{moneda}</span> : null}
      </span>
    );
  }

  return (
    <span className="relative flex items-center justify-end gap-1" data-salvare={stareSalvare}>
      <Money
        minor={minor}
        moneda={moneda}
        scalaMinora={scalaMinora}
        zecimale={zecimale}
        ariaLabel={eticheta}
      />

      {expresie ? (
        <span className="badge num" title={`Derivat din ${expresie}`} aria-hidden="true">
          =
        </span>
      ) : null}

      {stareSalvare !== 'curat' ? (
        <span
          className="indicator-salvare"
          data-stare={stareSalvare}
          aria-hidden="true"
          title={
            stareSalvare === 'eroare'
              ? (mesajEroare ?? 'Nesalvat')
              : stareSalvare === 'in-curs'
                ? 'Se salvează…'
                : stareSalvare === 'salvat'
                  ? 'Salvat'
                  : 'Modificat'
          }
        />
      ) : null}

      {stareSalvare === 'eroare' ? (
        <span className="doar-sr" role="status">
          {mesajEroare ?? 'Nu s-a putut salva.'}
        </span>
      ) : null}
    </span>
  );
}
